import { Button } from '@brightdrop/bd-ui';
import { useTranslations } from '@brightdrop/localization-client';
import { Role } from '@gm-commercial/profile-model';
import Box from '@mui/material/Box';
import { createSelector } from '@reduxjs/toolkit';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '~/app/store';
import InfoIcon from '~/assets/icons/new/infoIcon.svg?react';
import DelayedContent from '~/common/components/delayedContent/DelayedContent';
import TranslatedLabel, {
  translateText,
} from '~/common/components/TranslatedLabel/TranslatedLabel';
import useActingProfile from '~/common/hooks/useActingProfile';
import useProfileWithPermissions from '~/common/hooks/useProfileWithPermissions';
import { RouteName } from '~/common/models/route.model';
import { findRoute, makeRoutePath } from '~/common/utils/route/route.utils';
import {
  setActingProfile,
  setSelectedContext,
} from '~/store/profile/profileSlice';
import {
  selectActingProfileFleet,
  selectActingProfileHub,
  selectActingProfileOrganization,
  selectActingRole,
} from '~/store/profile/profileSlice.selectors';
import useStyles from './ScopedUserBanner.styles';

const MESSAGES = {
  roleList: {
    [`common:roleList.${Role.ORG_MANAGER}`]: 'Account Admin',
    [`common:roleList.${Role.HUB_MANAGER}`]: 'Hub Manager',
    [`common:roleList.${Role.FLEET_MANAGER}`]: 'Fleet Manager',
  },
};

const selectViewAsProfileParams = createSelector(
  selectActingRole,
  selectActingProfileOrganization,
  selectActingProfileHub,
  selectActingProfileFleet,
  (role, organization, hub, fleet) => ({ role, organization, hub, fleet })
);

const SUPPORTED_VIEW_AS_ROLES = [Role.ORG_MANAGER, Role.FLEET_MANAGER];

const ScopedUserBanner = (): JSX.Element => {
  const { classes } = useStyles();
  const { translations } = useTranslations(MESSAGES);
  // Subscribe to i18n so imperative `translateText(...)` updates on locale change.
  useTranslation(['organization', 'common']);
  const dispatch = useAppDispatch();
  const viewAsProfile = useSelector(selectViewAsProfileParams);
  const navigate = useNavigate();

  const { profile, isLoading, isUpdating } = useProfileWithPermissions();
  const { isActing } = useActingProfile();

  const isProfileInProgress = useMemo(
    () => isLoading || isUpdating,
    [isLoading, isUpdating]
  );
  const actingRole = useMemo(
    () =>
      viewAsProfile.role && SUPPORTED_VIEW_AS_ROLES.includes(viewAsProfile.role)
        ? viewAsProfile.role
        : undefined,
    [viewAsProfile.role]
  );

  const actingScope = useMemo(() => {
    if (actingRole) {
      switch (actingRole) {
        case Role.ORG_MANAGER: {
          return viewAsProfile.organization?.name;
        }
        case Role.FLEET_MANAGER: {
          return viewAsProfile.fleet?.name;
        }
      }
    }
    return undefined;
  }, [viewAsProfile, actingRole]);

  const handleActingProfileToggle = useCallback(() => {
    dispatch(setActingProfile());
    dispatch(setSelectedContext(undefined));
    navigate(makeRoutePath(findRoute(RouteName.ORGANIZATIONS), {}), {
      replace: true,
    });
  }, [dispatch, navigate]);

  return (
    <DelayedContent
      isLoading={isProfileInProgress}
      data={profile}
      skeleton={null}
    >
      {isActing && !isProfileInProgress && (
        <Box className={classes.bannerBackdrop}>
          <Box className={classes.pill}>
            <Box className={classes.messageContainer}>
              <InfoIcon className={classes.infoIcon} />
              <TranslatedLabel
                dataTestId="view-scoped-user-banner-message"
                typographyVariant="small_body_medium"
                translationKey={
                  actingScope
                    ? 'common:viewAs.headerMessage'
                    : 'common:viewAs.headerMessage_noName'
                }
                variables={{
                  role: translations.roleList[
                    `common:roleList.${actingRole}` as keyof (typeof translations)['roleList']
                  ],
                  entity: actingScope,
                }}
              />
            </Box>
            <Button
              variant="button-link"
              className={classes.exitContainer}
              onClick={handleActingProfileToggle}
              data-testid="remove-view-scoped-user-banner"
            >
              {translateText('organization:exitOrganization')}
            </Button>
          </Box>
        </Box>
      )}
    </DelayedContent>
  );
};

export default ScopedUserBanner;
